/**
 * A virtual dice roller — a phone-screen replacement for the physical dice
 * players roll at the start of a game (to decide seating) and before
 * building the wall (to decide where to break it). Purely decorative: it
 * never reads or writes any `Game` state, never touches scoring or dealer
 * rotation (see CLAUDE.md — those are derived algorithmically from
 * `game.rounds`, not from a dice roll). Available from both the main
 * device (`play.ts`) and a guest device (`guestPlay.ts`) — each device's
 * roll is entirely local, not synced through the relay, the same way two
 * people at a real table each just look at their own dice.
 *
 * Each die is a CSS 3D cube (6 faces, `transform-style: preserve-3d`)
 * rather than a real physics simulation — spinning it through a few
 * randomized full turns on each axis via a single CSS `transition` reads as
 * a convincing tumble without needing a physics-engine dependency (this
 * project deliberately has none). The maths behind it: a rotation of any
 * multiple of 360deg is exactly the identity rotation, so stacking extra
 * "spins" on top of the angle that shows the intended face never changes
 * the final resting orientation, only how many turns the browser animates
 * through to get there.
 *
 * Roll history: nothing stops someone from tapping Roll repeatedly to fish
 * for a bigger number — this is one phone, not a fair multiplayer RNG. What
 * a physical die relies on for fairness is everyone at the table watching
 * the same throw, so this keeps every roll of the current sitting visible
 * (`rollHistory`, module-level so it survives closing and reopening the
 * dialog — only a full page reload or the explicit "Clear Log" button
 * resets it, both conspicuous actions in front of the table, unlike quietly
 * closing and reopening the dialog would be) rather than only ever showing
 * the latest number. Re-rolling to cheat is still possible; doing it
 * invisibly isn't.
 */

const DICE_COUNT_KEY = 'mahjong-scorer:diceCount';

interface RollLogEntry {
  count: number;
  values: number[];
}

/** Module-level (not per-open) — see the file header's Roll history note. */
let rollHistory: RollLogEntry[] = [];
const MAX_HISTORY = 20;

/** Pip positions on a 3x3 grid (1-9, left-to-right/top-to-bottom), standard die layout. */
const PIP_LAYOUTS: Record<number, number[]> = {
  1: [5],
  2: [1, 9],
  3: [1, 5, 9],
  4: [1, 3, 7, 9],
  5: [1, 3, 5, 7, 9],
  6: [1, 3, 4, 6, 7, 9],
};

/** The (rotateX, rotateY) that brings each face's value to the front (facing the viewer) at rest. */
const FACE_ANGLES: Record<number, [number, number]> = {
  1: [0, 0],
  2: [-90, 0],
  3: [0, -90],
  4: [0, 90],
  5: [90, 0],
  6: [0, 180],
};

/** Which face position (fixed CSS class, see style.css) shows which value — front/back/left/right/top/bottom. */
const FACE_VALUES: { cls: string; value: number }[] = [
  { cls: 'dice-face-front', value: 1 },
  { cls: 'dice-face-top', value: 2 },
  { cls: 'dice-face-right', value: 3 },
  { cls: 'dice-face-left', value: 4 },
  { cls: 'dice-face-bottom', value: 5 },
  { cls: 'dice-face-back', value: 6 },
];

function renderFace(cls: string, value: number): string {
  const lit = new Set(PIP_LAYOUTS[value]);
  const pips = Array.from({ length: 9 }, (_, i) => (lit.has(i + 1) ? '<span class="pip"></span>' : '<span></span>'));
  return `<div class="dice-face ${cls}">${pips.join('')}</div>`;
}

function renderDie(): string {
  const faces = FACE_VALUES.map(({ cls, value }) => renderFace(cls, value)).join('');
  return `<div class="dice-scene"><div class="dice-cube">${faces}</div></div>`;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

function readStoredCount(): number {
  const stored = Number(localStorage.getItem(DICE_COUNT_KEY));
  return stored === 1 || stored === 2 || stored === 3 ? stored : 2;
}

/**
 * Rolls one die's cube to the given value. Stalls a moment before starting
 * (`extraDelayMs`) so 2-3 dice in the same roll don't all click to a stop in
 * perfect unison — real dice never do. After the animation settles, the
 * transform is snapped down to its small-angle equivalent (mathematically
 * identical — see the file header) so repeated rolls in one long session
 * don't accumulate ever-growing rotation values.
 */
function rollOneDie(cube: HTMLElement, value: number, extraDelayMs: number): void {
  const [bx, by] = FACE_ANGLES[value]!;

  if (prefersReducedMotion()) {
    cube.style.transition = 'none';
    cube.style.transform = `rotateX(${bx}deg) rotateY(${by}deg)`;
    return;
  }

  const spins = 2 + Math.floor(Math.random() * 2);
  const rx = bx + 360 * spins;
  const ry = by + 360 * (spins + Math.floor(Math.random() * 2));
  const rz = 360 * (spins + 1 + Math.floor(Math.random() * 2));
  const duration = 1000 + Math.random() * 300;

  cube.style.transition = `transform ${duration}ms cubic-bezier(0.34, 1.35, 0.64, 1) ${extraDelayMs}ms`;
  // Force a reflow so a re-roll from an already-settled transform still
  // picks up the new transition/transform as a fresh animation, rather than
  // the browser coalescing two synchronous style writes into one.
  void cube.offsetWidth;
  cube.style.transform = `rotateX(${rx}deg) rotateY(${ry}deg) rotateZ(${rz}deg)`;

  cube.addEventListener(
    'transitionend',
    () => {
      cube.style.transition = 'none';
      cube.style.transform = `rotateX(${bx}deg) rotateY(${by}deg)`;
    },
    { once: true },
  );
}

function randomFace(): number {
  return 1 + Math.floor(Math.random() * 6);
}

function formatRollSummary(values: number[]): string {
  return values.length > 1 ? `${values.join(' + ')} = ${values.reduce((a, b) => a + b, 0)}` : `${values[0]}`;
}

function formatLogEntry(entry: RollLogEntry, rollNumber: number): string {
  const dieWord = entry.count === 1 ? 'die' : 'dice';
  return `Roll ${rollNumber} (${entry.count} ${dieWord}): ${formatRollSummary(entry.values)}`;
}

let overlay: HTMLDivElement | undefined;

function close(): void {
  overlay?.remove();
  overlay = undefined;
  document.removeEventListener('keydown', onKeydown);
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') close();
}

/**
 * Opens the dice-roller overlay. Call from a button on play.ts or
 * guestPlay.ts. Reuses the same overlay/panel classes as
 * `houseRulesPreview.ts`/`themePicker.ts` (`.lightbox-overlay` /
 * `.card modal-panel`) rather than inventing new ones — same
 * backdrop-closes-it, panel-click-doesn't, Escape-closes-it behavior.
 */
export function openDiceRoller(): void {
  close();

  const el = document.createElement('div');
  el.className = 'lightbox-overlay';
  el.innerHTML = `
    <div class="card modal-panel dice-panel">
      <h1 style="margin-top:0">Roll Dice</h1>
      <div class="row">
        <button type="button" class="secondary dice-count-btn" data-count="1">1 Die</button>
        <button type="button" class="secondary dice-count-btn" data-count="2">2 Dice</button>
        <button type="button" class="secondary dice-count-btn" data-count="3">3 Dice</button>
      </div>
      <div class="dice-tray" id="diceTray"></div>
      <p class="dice-result" id="diceResultText">&nbsp;</p>
      <button type="button" id="rollDiceBtn" style="width:100%;margin-top:0.5rem">Roll</button>
      <button type="button" id="diceCloseBtn" class="ghost" style="width:100%;margin-top:0.5rem">Close</button>
      <div class="dice-log" id="diceLog"></div>
    </div>
  `;
  // Tapping the backdrop closes it; tapping inside the panel shouldn't.
  el.addEventListener('click', close);
  el.querySelector('.modal-panel')!.addEventListener('click', (e) => e.stopPropagation());
  document.addEventListener('keydown', onKeydown);

  document.body.appendChild(el);
  overlay = el;

  const tray = el.querySelector<HTMLDivElement>('#diceTray')!;
  const resultText = el.querySelector<HTMLParagraphElement>('#diceResultText')!;
  const countBtns = [...el.querySelectorAll<HTMLButtonElement>('.dice-count-btn')];
  const log = el.querySelector<HTMLDivElement>('#diceLog')!;
  const rollBtn = el.querySelector<HTMLButtonElement>('#rollDiceBtn')!;

  let count = readStoredCount();

  function renderTray(): void {
    tray.innerHTML = Array.from({ length: count }, () => renderDie()).join('');
    for (const btn of countBtns) {
      btn.classList.toggle('is-selected', Number(btn.dataset['count']) === count);
    }
    resultText.innerHTML = '&nbsp;';
  }

  function renderLog(): void {
    if (rollHistory.length === 0) {
      log.innerHTML = '';
      return;
    }
    const rows = rollHistory
      .map((entry, i) => {
        const isLatest = i === rollHistory.length - 1;
        return `<li class="${isLatest ? 'is-latest' : ''}">${formatLogEntry(entry, i + 1)}</li>`;
      })
      .join('');
    log.innerHTML = `
      <p class="muted" style="margin:0.75rem 0 0.25rem">Rolls this sitting (visible to everyone, not just the latest):</p>
      <ul class="dice-log-list">${rows}</ul>
      <button type="button" id="clearDiceLogBtn" class="ghost" style="width:100%;margin-top:0.25rem">Clear Log</button>
    `;
    log.querySelector<HTMLButtonElement>('#clearDiceLogBtn')!.addEventListener('click', () => {
      rollHistory = [];
      renderLog();
    });
  }

  for (const btn of countBtns) {
    btn.addEventListener('click', () => {
      count = Number(btn.dataset['count']);
      localStorage.setItem(DICE_COUNT_KEY, String(count));
      renderTray();
    });
  }

  rollBtn.addEventListener('click', () => {
    const cubes = [...tray.querySelectorAll<HTMLDivElement>('.dice-cube')];
    const values = cubes.map(() => randomFace());
    cubes.forEach((cube, i) => rollOneDie(cube, values[i]!, i * 120));

    const settleMs = prefersReducedMotion() ? 0 : 1400;
    resultText.innerHTML = '&nbsp;';
    // Disabled only long enough to cover the tumble animation — a double-tap
    // mid-roll would otherwise interrupt the transition, not a cooldown
    // meant to slow down re-rolling (see the file header: that's handled by
    // keeping every roll visible in the log instead, not by making it slow).
    rollBtn.disabled = true;
    window.setTimeout(() => {
      resultText.textContent = formatRollSummary(values);
      rollHistory = [...rollHistory, { count, values }].slice(-MAX_HISTORY);
      renderLog();
      rollBtn.disabled = false;
    }, settleMs);
  });

  el.querySelector<HTMLButtonElement>('#diceCloseBtn')!.addEventListener('click', close);

  renderTray();
  renderLog();
}
